import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AssetDto, AssetVersionDto, GalleryDto, UserAssetUploadBodyDto } from '@picflow/types';
import { BehaviorSubject, Observable, Subject, Subscriber, Subscription, lastValueFrom } from 'rxjs';
import { OutOfStorageDialogComponent } from '../account/dialogs/out-of-storage-dialog/out-of-storage-dialog.component';
import { RawUploadPaywallDialogComponent } from '../account/dialogs/raw-upload-paywall-dialog/raw-upload-paywall-dialog.component';
import { VideoBetaDialogComponent } from '../account/dialogs/video-beta-dialog/video-beta-dialog.component';
import { ChecklistService } from '../checklist.service';
import { UploadObj, UploadObjError } from '../classes/upload-obj.class';
import {
  allowedRawTypes,
  allowedTypes,
  allowedVideoTypes,
  maxConcurrentUploads,
  maxFileSize,
  maxPostAssetRetryCount,
  maxUploadFullRetryCount,
  maxUploadRetryCount,
} from '../consts';
import { sortAssetSection } from '../gallery/lib/sort-asset-section';
import { getPermissions } from '../lib/get-allowed-feedback-options';
import { getFileContentType } from '../lib/get-file-type';
import { randomString } from '../lib/random-string';
import { removeExtension } from '../lib/remove-extension';
import { AppConfigService } from '../providers/config.service';
import { AnalyticsService } from './analytics.service';
import { AssetService } from './asset.service';
import { DialogService } from './dialog.service';
import { DuplicateAssetService } from './duplicate-asset.service';
import { FeatureFlagsService } from './feature-flags.service';
import { NetworkService } from './network.service';
import { SocketService } from './socket.service';
import { StateService } from './state.service';
import { ToastService } from './toast.service';
import { UpdateService } from './update.service';

type UploadObserver = Subscriber<
  | { type: 'created' | 'uploaded'; data: { asset?: AssetDto; version?: AssetVersionDto; upload: UploadObj } }
  | { type: 'init'; uploads: UploadObj[] }
  | { type: 'remove'; data: { upload: UploadObj } }
>;

@Injectable({
  providedIn: 'root',
})
export class UploadService {
  private pendingUploads: { upload: UploadObj; resolve: (value: unknown) => void }[] = [];
  private activeUploads: Set<UploadObj> = new Set();
  public uploads: UploadObj[] = [];
  private _uploadEvents: Subject<UploadObj> = new BehaviorSubject<UploadObj>(undefined);
  public readonly onUploadEvent: Observable<UploadObj> = this._uploadEvents.asObservable();
  public currentUploadSpeed: number;

  constructor(
    private http: HttpClient,
    private config: AppConfigService,
    private dialog: DialogService,
    private assetService: AssetService,
    private socketService: SocketService,
    private networkService: NetworkService,
    private state: StateService,
    private analytics: AnalyticsService,
    private toasts: ToastService,
    private duplicateService: DuplicateAssetService,
    private checklistService: ChecklistService,
    private featureFlags: FeatureFlagsService,
    private updateService: UpdateService
  ) {}

  private startAllowedPending() {
    if (this.activeUploads.size >= maxConcurrentUploads) return;
    if (!this.pendingUploads.length) return;
    const winner = this.pendingUploads.shift();
    this.activeUploads.add(winner.upload);
    winner.resolve(true);
    this.startAllowedPending();
  }

  private async onUploadAllowed(upload: UploadObj) {
    let promise = new Promise(resolve => this.pendingUploads.push({ upload, resolve }));
    this.startAllowedPending();
    return promise;
  }

  private markUploadAsError(upload: UploadObj, error: UploadObjError = 'general') {
    upload.status = 'error';
    upload.error = error;
    upload.progress = 100;
    this.activeUploads.delete(upload);
    this._uploadEvents.next(upload);
    this.startAllowedPending();
  }

  private markUploadAsCancelled(upload: UploadObj) {
    upload.status = 'error';
    upload.error = 'cancelled';
    upload.progress = 100;
    this.activeUploads.delete(upload);
    this._uploadEvents.next(upload);
    this.startAllowedPending();
  }

  private markUploadAsUploading(upload: UploadObj) {
    upload.status = 'started';
    upload.progress = 0;
    this._uploadEvents.next(upload);
  }

  private markUploadAsDone(upload: UploadObj) {
    upload.endTime = Date.now();
    this.activeUploads.delete(upload);
    upload.status = 'done';
    upload.progress = 100;
    this._uploadEvents.next(upload);
    this.startAllowedPending();
  }

  public async waitForAssetProcessing(assetId: string): Promise<AssetDto> {
    this.socketService.connect();
    return new Promise(async resolve => {
      let sub: Subscription;
      sub = this.socketService.onSocketEvent.subscribe(data => {
        if (!data) return;
        if (data.name !== 'asset.updated') return;
        if (data.payload.asset.id !== assetId) return;
        if (data.payload.asset.version_data['status'] === 'pending') return;
        return resolve(data.payload.asset);
      });
    });
  }

  public removeUploads(galleryId: string) {
    this.uploads = this.uploads.filter(upload => upload.galleryId !== galleryId);
  }

  public cancelUploads(galleryId: string) {
    this.uploads.filter(upload => upload.galleryId === galleryId).forEach(upload => (upload.cancelled = true));
  }

  public allowedFileType(file: File, gallery: GalleryDto): boolean {
    if (!file) return false;

    allowedTypes.push(...allowedRawTypes);

    if (this.featureFlags.get('video')) {
      allowedTypes.push(...allowedVideoTypes);
    }

    const contentType = getFileContentType(file);

    if (contentType.split('/')[0] !== 'image' && contentType.split('/')[0] !== 'video') return false;
    const type = contentType.split('/')[1].toLowerCase();

    const permissions = getPermissions(gallery, this.state.plan);

    if (allowedRawTypes.includes(type) && !permissions.asset.uploadRaw) {
      if (!this.dialog.dialogOpen) {
        console.log('File format not allowed due to plan', type);
        this.dialog.open(RawUploadPaywallDialogComponent);
      }
      this.uploads.splice(
        this.uploads.findIndex(upload => upload.file === file),
        1
      );
      return false;
    }

    if (allowedVideoTypes.includes(type) && !this.featureFlags.get('video')) {
      this.dialog.open(VideoBetaDialogComponent);
      this.uploads.splice(
        this.uploads.findIndex(upload => upload.file === file),
        1
      );
      return false;
    }

    return allowedTypes.includes(type);
  }

  public async uploadUserAsset(file: File): Promise<string> {
    const params: UserAssetUploadBodyDto = {
      content_type: file.type,
      content_length: file.size,
    };
    const response = <{ upload_url: string; url: string }>(
      await lastValueFrom(this.http.post(this.config.apiEndpoint + '/assets/upload/user_asset', params))
    );
    await lastValueFrom(this.http.put(response.upload_url, file));
    return response.url;
  }

  public async uploadTenantAsset(file: File): Promise<string> {
    const params: UserAssetUploadBodyDto = {
      content_type: file.type,
      content_length: file.size,
    };
    const response = <{ upload_url: string; url: string }>(
      await lastValueFrom(this.http.post(this.config.apiEndpoint + '/assets/upload/tenant_asset', params))
    );
    await lastValueFrom(this.http.put(response.upload_url, file));
    return response.url;
  }

  public handleCanUpload(files: File[]) {
    const resp = this.canUpload(files);
    if (!resp) return;
    if (!resp.possible) {
      this.dialog.open(OutOfStorageDialogComponent, { over: resp.over });
      return false;
    }
    return true;
  }

  private calcNewPositions(assets: AssetDto[], files: File[], sorting: string) {
    if (sorting === 'manual') {
      let highest = Math.max(...assets.map(asset => asset.position));
      const map = new Map();
      files.forEach(file => {
        highest += 1;
        map.set(file.name, highest);
      });
      return map;
    }
    const allAssets = assets.concat(
      files.map(
        file =>
          <AssetDto>{
            id: randomString(12),
            position: 9999999999,
            version_data: { asset_name: file.name, created_at: Date.now() },
          }
      )
    );
    const sortedAssets = sortAssetSection(allAssets, sorting || 'manual');
    const map: Map<string, number> = new Map();
    const positions = this.assetService.calcOrderChanges(sortedAssets);

    sortedAssets.forEach((asset, i) => {
      const position = positions.find(pos => pos.id === asset.id);
      map.set(asset.version_data.asset_name, position?.pos ?? i);
    });

    return map;
  }

  public async uploadSingle(file: File, gallery: GalleryDto, sectionId: string) {
    const upload = new UploadObj(file, gallery.id, sectionId);

    if (!this.handleCanUpload([file])) {
      this.markUploadAsError(upload, 'invalid-file-type');
      return;
    }

    if (!this.allowedFileType(upload.file, gallery)) {
      this.markUploadAsError(upload, 'invalid-file-type');
      return;
    }

    if (upload.file.size > maxFileSize) {
      this.markUploadAsError(upload, 'file-too-big');
      return;
    }

    this.uploads.push(upload);
    this.markUploadAsUploading(upload);
    const asset = await this.assetService.postAsset({
      galleryId: gallery.id,
      sectionId,
      contentLength: upload.file.size,
      contentType: upload.file.type,
      name: upload.file.name,
      position: upload.position,
    });
    await lastValueFrom(this.http.put(asset.version_data['upload_url'], upload.file));
    this.markUploadAsDone(upload);
    return asset;
  }

  public async uploadNewVersion(asset: AssetDto, file: File) {
    const upload = new UploadObj(file, <string>asset.gallery, <string>asset.section);
    this.uploads.push(upload);
    this.markUploadAsUploading(upload);
    let version: AssetVersionDto;
    try {
      version = await this.assetService.postVersion(asset.id, {
        contentLength: file.size,
        contentType: file.type,
        name: file.name,
        galleryId: upload.galleryId,
      });
    } catch (err) {
      if (!(err instanceof HttpErrorResponse)) throw err;
      if (err.error.code !== 'unsupported_asset_type') throw err;
      this.markUploadAsError(upload, 'wrong-type');
      return;
    }

    await lastValueFrom(this.http.put(version['upload_url'], upload.file));
    this.markUploadAsDone(upload);
    return version;
  }

  private async createAssetOrVersion(
    upload: UploadObj,
    gallery: GalleryDto,
    sectionId: string,
    assetToReplace?: AssetDto
  ): Promise<{ asset: AssetDto; version: AssetVersionDto }> {
    try {
      if (assetToReplace) {
        const version = await this.createAssetVersionWithRetries(upload, assetToReplace);
        return { asset: undefined, version };
      } else {
        const asset = await this.createAssetWithRetries(upload, gallery, sectionId);
        return { asset, version: undefined };
      }
    } catch (err) {
      if (!(err instanceof HttpErrorResponse)) throw err;
      return { asset: undefined, version: undefined };
    }
  }

  private async createAssetWithRetries(
    upload: UploadObj,
    gallery: GalleryDto,
    sectionId: string,
    retries = 0
  ): Promise<AssetDto> {
    if (retries) await this.networkService.onNetworkOnline();
    try {
      return await this.assetService.postAsset({
        galleryId: gallery.id,
        sectionId,
        contentLength: upload.file.size,
        contentType: upload.file.type,
        name: upload.file.name,
        position: upload.position,
      });
    } catch (err) {
      if (!(err instanceof HttpErrorResponse)) throw err;
      if (retries >= maxPostAssetRetryCount) throw err;
      return this.createAssetWithRetries(upload, gallery, sectionId, retries + 1);
    }
  }

  private async createAssetVersionWithRetries(
    upload: UploadObj,
    assetToReplace: AssetDto,
    retries = 0
  ): Promise<AssetVersionDto> {
    if (retries) await this.networkService.onNetworkOnline();
    try {
      return await this.assetService.postVersion(assetToReplace.id, {
        contentLength: upload.file.size,
        contentType: upload.file.type,
        name: upload.file.name,
        galleryId: upload.galleryId,
      });
    } catch (err) {
      if (!(err instanceof HttpErrorResponse)) throw err;
      if (retries >= maxPostAssetRetryCount) throw err;
      return this.createAssetVersionWithRetries(upload, assetToReplace, retries + 1);
    }
  }

  private async uploadFileWithRetries(uploadUrl: string, file: File, retries = 0): Promise<void> {
    if (retries) await this.networkService.onNetworkOnline();
    try {
      await lastValueFrom(this.http.put(uploadUrl, file));
    } catch (err) {
      console.warn(err);
      if (!(err instanceof HttpErrorResponse)) throw err;
      if (retries >= maxUploadRetryCount) throw err;
      return this.uploadFileWithRetries(uploadUrl, file, retries + 1);
    }
  }

  private async managedUploadSingle(
    observer: UploadObserver,
    upload: UploadObj,
    assetToReplace?: AssetDto,
    gallery?: GalleryDto,
    sectionId?: string,
    retries = 0
  ): Promise<AssetDto> {
    if (!this.allowedFileType(upload.file, gallery)) {
      this.markUploadAsError(upload, 'invalid-file-type');
      observer.next({ type: 'remove', data: { upload } });
      return;
    }

    if (upload.file.size > maxFileSize) {
      this.markUploadAsError(upload, 'file-too-big');
      observer.next({ type: 'remove', data: { upload } });
      return;
    }

    this.markUploadAsUploading(upload);
    if (retries && !assetToReplace) observer.next({ type: 'remove', data: { upload } });
    const { asset, version } = await this.createAssetOrVersion(upload, gallery, sectionId, assetToReplace);
    if (!version && !asset) {
      this.markUploadAsError(upload);
      observer.next({ type: 'remove', data: { upload } });
      return;
    }

    if (upload.cancelled) {
      this.markUploadAsCancelled(upload);
      return;
    }

    if (asset) {
      observer.next({ type: 'created', data: { asset, upload } });
    }

    const uploadUrl = (asset?.version_data || version)['upload_url'];

    try {
      await this.uploadFileWithRetries(uploadUrl, upload.file);
    } catch (err) {
      if (!(err instanceof HttpErrorResponse)) throw err;
      if (retries >= maxUploadFullRetryCount) {
        this.markUploadAsError(upload);
        observer.next({ type: 'remove', data: { upload } });
        return;
      }
      return this.managedUploadSingle(observer, upload, assetToReplace, gallery, sectionId, retries + 1);
    }

    this.markUploadAsDone(upload);
    observer.next({ type: 'uploaded', data: { asset, upload } });
    return asset;
  }

  private async internalManagedUpload(
    observer: UploadObserver,
    _files: File[],
    gallery: GalleryDto,
    sectionId: string,
    allAssets: AssetDto[],
    sorting: string
  ) {
    // Getting files / checking for duplicates
    const { files, assetsToReplace } = await this.duplicateService.handleDuplicateFiles(
      _files,
      gallery.id,
      this.uploads
    );

    if (!files?.length) {
      observer.complete();
      return;
    }

    const resp = this.handleCanUpload(files);
    if (!resp) {
      observer.complete();
      return;
    }

    const positionMap: Map<string, number> = this.calcNewPositions(allAssets, files, sorting);
    const uploads = files.map(file => new UploadObj(file, gallery.id, sectionId, positionMap.get(file.name)));

    this.analytics.track('upload_started', true, { uploads: uploads.length });

    window.onbeforeunload = function (event: any) {
      var e = event || window.event;
      if (e) e.returnValue = 'Are you sure? The upload is not completed yet.';
      return 'Are you sure? The upload is not completed yet.';
    };

    uploads.sort((a, b) => a.position - b.position);
    observer.next({ type: 'init', uploads });

    const assets = (
      await Promise.all(
        uploads.map(async upload => {
          const nameWithoutExtension = removeExtension(upload.file.name);
          const assetToReplace = assetsToReplace.find(
            i => removeExtension(i.version_data.asset_name) === nameWithoutExtension
          );

          this._uploadEvents.next(upload);
          this.uploads.push(upload);
          await this.onUploadAllowed(upload);

          return this.managedUploadSingle(observer, upload, assetToReplace, gallery, sectionId);
        })
      )
    ).filter(img => img);

    // Checking if the user has cancelled the upload
    const cancelled = uploads.find(upload => upload.cancelled);
    if (cancelled) {
      this.analytics.track('upload_cancelled');
      this.toasts.add('Upload cancelled', 'success');
      await Promise.all(assets.map(asset => this.assetService.deleteAsset(asset)));
      observer.complete();
      window.onbeforeunload = null;
      return;
    }

    window.onbeforeunload = null;

    this.analytics.track('upload_success');
    observer.complete();
    this.checklistService.markItemAsDone('uploadAssets');
  }

  public managedUpload(
    _files: FileList,
    gallery: GalleryDto,
    sectionId: string,
    allAssets: AssetDto[],
    sorting: string
  ) {
    this.updateService.subscribeGallery(gallery.id); // making sure the gallery that we are subscribed to the gallery
    return new Observable((observer: UploadObserver) => {
      const files = Array.from(_files);
      this.internalManagedUpload(observer, files, gallery, sectionId, allAssets, sorting);
    });
  }

  private canUpload(files: File[]): { possible: boolean; over: number } {
    if (!this.state.user) return;
    if (!this.state.plan) return { possible: true, over: 0 };
    const toUpload = files.reduce((t, f) => t + f.size / 1024, 0); // total uploads size in kb
    const capacity = this.state.plan ? this.state.plan.limits.storage.active.total : 999999999; // capacity in kb
    const remainingCapacity = capacity - this.state.activeTenant['storage'].active; // remaining capacity in kb
    return { possible: toUpload < remainingCapacity, over: toUpload - remainingCapacity };
  }
}
